import type { DragSort } from '../ui/useDragSort';
import type { BoardEntry } from '../db/board';
import type { Item, Section } from '../db/schema';
import type { Placement } from './masonry';
import { Tile } from './Tile';
import { SectionTile } from './SectionTile';
import type { Selection } from './useSelection';

/**
 * The tiles, the hole a dragged one will drop into, and nothing else. Split
 * from `Board` because the two have nothing to say to each other: this is
 * where things are, that is what things there are.
 */
export function BoardGrid({
  entries,
  placements,
  roomId,
  base,
  openItemId,
  highlight,
  animate,
  sortable,
  selection,
  mergeCovers,
  gridRef,
  height,
  onMoveItem,
  onDeleteItem,
  onEditSection,
  onDeleteSection,
}: {
  entries: readonly BoardEntry[];
  placements: Map<string, Placement>;
  roomId: string;
  /** This container's address, which an item's panel opens on top of. */
  base: string;
  openItemId: string | undefined;
  /** The search that filtered this board, marked in the captions. */
  highlight: string | undefined;
  animate: boolean;
  sortable: DragSort;
  selection: Selection;
  /** The covers a grouping would put in the new section, dragged one first. */
  mergeCovers: readonly string[];
  gridRef: (node: HTMLElement | null) => void;
  height: number;
  onMoveItem: (item: Item) => void;
  onDeleteItem: (item: Item) => void;
  onEditSection: (section: Section) => void;
  onDeleteSection: (section: Section) => void;
}) {
  const hole =
    sortable.draggingId === undefined ? undefined : placements.get(sortable.draggingId);

  return (
    <div
      className={`board__grid${sortable.draggingId === undefined ? '' : ' sorting'}`}
      ref={gridRef}
      style={{ height: `${height}px` }}
    >
      {/* The masonry still gives the lifted tile a slot — it's the tile that's
          elsewhere — so this only draws the space that already exists. */}
      {hole !== undefined && (
        <div
          className="board__hole"
          aria-hidden="true"
          style={{
            transform: `translate(${hole.x}px, ${hole.y}px)`,
            width: `${hole.w}px`,
            height: `${hole.h}px`,
          }}
        />
      )}

      {entries.map((entry) => {
        const placement = placements.get(entry.id);
        // One frame, between something landing in the database and the layout
        // pass that places it.
        if (placement === undefined) return null;

        const shared = {
          placement,
          animate,
          lifted: sortable.draggingId === entry.id,
          merging: sortable.mergeId === entry.id,
          sort: sortable.bind(entry.id),
        };

        return entry.kind === 'item' ? (
          <Tile
            key={entry.id}
            {...shared}
            base={base}
            item={entry.item}
            selected={entry.id === openItemId}
            picked={selection.has(entry.id)}
            highlight={highlight}
            mergeCovers={mergeCovers}
            onPick={(modifiers) => selection.click(entry.id, modifiers)}
            onMove={() => onMoveItem(entry.item)}
            onDelete={() => onDeleteItem(entry.item)}
          />
        ) : (
          <SectionTile
            key={entry.id}
            {...shared}
            roomId={roomId}
            section={entry.section}
            count={entry.count}
            // Held over: show what it would hold, newest first, so the
            // preview matches what opening it will look like.
            covers={
              sortable.mergeId === entry.id
                ? [...mergeCovers.slice(0, 1), ...entry.covers].slice(0, 4)
                : entry.covers
            }
            onEdit={() => onEditSection(entry.section)}
            onDelete={() => onDeleteSection(entry.section)}
          />
        );
      })}
    </div>
  );
}
